import {
  CarbonIntensity,
  CarbonIntensities
} from '@/models/carbon/CarbonIntensity.js'
import {
  ImpactIntensity,
  ImpactIntensities
} from '@/models/carbon/ImpactIntensity.js'
import WASTES_FACTORS from '@/../data/factors/carbon/wastesFactors.json'
import WASTES_LCA_FACTORS from '@/../data/factors/lca/wastesFactors.json'
import Model from '@/models/carbon/Model.js'
import Modules from '@/models/Modules.js'
import { EmissionFactor, ImpactFactor } from '@/models/carbon/Factor.js'
import { floatConverter, tagsConverter } from '@/utils/parser.js'

const DEFAULT_GROUP = 'packaging'
const DEFAULT_TYPE = 'wood'
const DEFAULT_VALORIZATION = 'incineration'

export default class Waste extends Model {
  constructor ({
    id = null,
    group = DEFAULT_GROUP,
    type = DEFAULT_TYPE,
    valorization = DEFAULT_VALORIZATION,
    amount = 0,
    tags = [],
    source = null
  } = {}) {
    super({ tags, source })
    this.id = id
    this.group = group
    this.type = type
    this.valorization = valorization
    this.amount = this.setAmount(amount)
    this.intensity = new CarbonIntensity()
    this.impact = new ImpactIntensity()
  }

  descriptor () {
    return this.group + '_' + this.type + '_' + this.valorization
  }

  isSimilarTo (other) {
    return (
      this === other ||
      this.id === other.id ||
      this.descriptor === other.descriptor
    )
  }

  get score () {
    let score = 1
    if (!this.group || !this.type || !this.valorization || !this.amount) {
      score = 2
    }
    return score
  }

  clean () {
    // coerce to float amount
    // (input form might set this as string)
    this.amount = parseFloat(this.amount)
  }

  toString (sep = '\t') {
    let values = [
      this.group,
      this.type,
      this.valorization,
      this.amount,
      this.tags.map((val) => val.name).join(';'),
      Math.round(this.intensity.intensity),
      Math.round(this.intensity.uncertainty)
    ]
    for (let key of ImpactFactor.keys) {
      if (Object.keys(this.impact.intensity).includes(key)) {
        values.push(this.impact.intensity[key])
      } else {
        values.push(0)
      }
    }
    return values.join(sep)
  }

  isValid () {
    return this.score === 1
  }

  isIncomplete () {
    return false
  }

  isInvalid () {
    return this.score === 2
  }

  setAmount (value) {
    let amount = value
    if (typeof amount === 'string') {
      amount = amount.replace(',', '.').trim().replace(/\s/g, '')
    }
    if (!isNaN(amount)) {
      amount = parseFloat(amount)
    }
    return amount
  }

  getCarbonIntensity (year = null) {
    let ef = EmissionFactor.createFromObj(
      WASTES_FACTORS[this.group][this.type][this.valorization]
    )
    ef = ef.getFactor(year)
    let intensity = new CarbonIntensity(
      this.amount * ef.total.total,
      this.amount * ef.total.total * ef.total.uncertainty,
      ef.group
    )
    this.intensity = intensity
    return intensity
  }

  getImpact (year = null) {
    let ifactors = ImpactFactor.createFromObj(
      WASTES_LCA_FACTORS[this.group][this.type][this.valorization]
    )
    let impact = ImpactIntensity.compute(this.amount, ifactors)
    this.impact = impact
    return impact
  }

  toDatabase () {
    return {
      group: this.group,
      type: this.type,
      valorization: this.valorization,
      amount: this.amount,
      tags: this.tags
    }
  }

  static get formatDescription () {
    return {
      group: {
        pattern: /(group|waste.group|wastegroup|groupe|group.de.dechet)/i,
        required: true,
        default: '' // considered as invalid
      },
      type: {
        pattern: /(type|waste.type|wastetype|waste|dechet|type.de.dechet)/i,
        required: true,
        default: '' // considered as invalid
      },
      treatment: {
        pattern: /(treatment|treatment.sector|filiere|filiere.de.traitement)/i,
        required: true,
        default: '' // considered as invalid
      },
      amount: {
        pattern: /(quantite|amount|total|poids|weight)/i,
        converter: floatConverter,
        required: true,
        default: null // considered as invalid
      },
      tags: {
        pattern: /(tags?|labels?)/i,
        required: false,
        converter: tagsConverter,
        default: []
      }
    }
  }

  aggregateDescriptor () {
    return [this.group, this.type, this.valorization, this.tagsHash].join('_')
  }

  _doAggregate (other) {
    let newWaste = Waste.createFromObj(this)
    newWaste.amount += other.amount
    return newWaste
  }

  static get MODULE_NAME () {
    return Modules.WASTES
  }

  static getGroups () {
    let groups = Object.keys(WASTES_FACTORS)
    return Array.from(new Set(groups))
  }

  static getTypes (group) {
    let types = Object.keys(WASTES_FACTORS[group])
    return Array.from(new Set(types))
  }

  static getValorizations (group, type) {
    let valorizations = Object.keys(WASTES_FACTORS[group][type])
    return Array.from(new Set(valorizations))
  }

  static createFromObj (item) {
    return new Waste({
      id: item.id,
      group: item.group,
      type: item.type,
      valorization: item.valorization,
      amount: item.amount,
      tags: item.tags,
      source: item.source
    })
  }

  /**
   * @param {Object[]} lines - The list of wastes object to handle
   * @param {String} tags - The list of tags defined by the user
   * @param {String} source - The source file name
   *
   * @returns {Waste[]} - The list of waste objects
   */
  static createFromLines (lines, format, tags, source) {
    let wastes = []
    for (let line of lines) {
      let nitem = Waste.createFromObj({
        id: null,
        group: line.group,
        type: line.type,
        valorization: line.valorization,
        amount: line.amount,
        source: source
      })
      nitem.addTags(Model.getTagsFromNames(line.tags, tags))
      wastes.push(nitem)
    }
    return wastes
  }

  static exportHeader (sep = '\t') {
    let values = [
      'group',
      'type',
      'valorization',
      'amount',
      'tags',
      'emission.kg.co2e',
      'uncertainty.kg.co2e'
    ]
    for (let key of ImpactFactor.keys) {
      values.push(key)
    }
    return values.join(sep)
  }

  static exportToFile (items, header = true, extraColValue = null, sep = '\t') {
    let headerValues = []
    if (header) {
      headerValues = Waste.exportHeader(sep)
    }
    return [
      headerValues,
      ...items.map(function (item) {
        let val = ''
        if (extraColValue) {
          val += extraColValue + sep
        }
        return val + item.toString(sep)
      })
    ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')
  }

  static compute (items, year, tags = []) {
    let intensities = new CarbonIntensities()
    let impacts = new ImpactIntensities()
    for (let item of items.filter((obj) => obj.hasTags(tags))) {
      let intensity = item.getCarbonIntensity(year)
      let impact = item.getImpact(year)
      intensities.add(intensity)
      impacts.add(impact)
    }
    return {
      intensity: intensities.sum(),
      impact: impacts.sum()
    }
  }
}
